// frontend/src/components/ReportButton.jsx
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Flag, Loader2, X, AlertTriangle } from "lucide-react";
import Swal from "sweetalert2";
import api from "../config/api";

const REPORT_REASONS = [
  "Conteúdo ofensivo ou discriminatório",
  "Produto não existe ou é falso",
  "Vendedor não responde",
  "Preço abusivo ou enganoso",
  "Informações enganosas",
  "Spam ou publicidade indevida",
  "Fraude ou golpe",
  "Outro motivo",
];

export default function ReportButton({ postId, postTitle, currentUser }) {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedReason, setSelectedReason] = useState("");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);

  const handleOpenModal = () => {
    if (!currentUser) {
      Swal.fire({
        icon: "info",
        title: "Faça login",
        text: "Você precisa estar logado para denunciar um anúncio.",
        confirmButtonColor: "#449879",
        confirmButtonText: "Fazer login",
        showCancelButton: true,
        cancelButtonText: "Cancelar",
        customClass: {
          popup: "rounded-2xl",
          confirmButton: "rounded-xl px-6 py-2.5",
          cancelButton: "rounded-xl px-6 py-2.5",
        },
      }).then((result) => {
        if (result.isConfirmed) {
          navigate("/login");
        }
      });
      return;
    }

    if (currentUser.status !== "aprovado") {
      Swal.fire({
        icon: "warning",
        title: "Conta em validação",
        text: "Aguarde a aprovação da sua conta para denunciar anúncios.",
        confirmButtonColor: "#449879",
        customClass: {
          popup: "rounded-2xl",
          confirmButton: "rounded-xl px-6 py-2.5",
        },
      });
      return;
    }

    setIsModalOpen(true);
  };

  const handleSubmitReport = async () => {
    if (!selectedReason) {
      Swal.fire({
        icon: "warning",
        title: "Selecione um motivo",
        text: "Por favor, selecione um motivo para a denúncia.",
        confirmButtonColor: "#449879",
        customClass: {
          popup: "rounded-2xl",
          confirmButton: "rounded-xl px-6 py-2.5",
        },
      });
      return;
    }

    setLoading(true);

    try {
      const response = await api.post("/reports", {
        post_id: postId,
        reason: selectedReason,
        description: description || "Sem descrição adicional.",
      });

      if (response.data.success) {
        Swal.fire({
          icon: "success",
          title: "Denúncia enviada!",
          text: "A equipe irá analisar e tomar as medidas necessárias.",
          confirmButtonColor: "#449879",
          timer: 3000,
          showConfirmButton: false,
          customClass: {
            popup: "rounded-2xl",
          },
        });
        setIsModalOpen(false);
        setSelectedReason("");
        setDescription("");
      }
    } catch (error) {
      console.error("Error al enviar denúncia:", error);

      let errorMessage = "Não foi possível enviar a denúncia. Tente novamente.";
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      if (error.response?.data?.errors) {
        const errors = Object.values(error.response.data.errors).flat();
        errorMessage = errors.join("\n");
      }

      Swal.fire({
        icon: "error",
        title: "Erro",
        text: errorMessage,
        confirmButtonColor: "#449879",
        customClass: {
          popup: "rounded-2xl",
          confirmButton: "rounded-xl px-6 py-2.5",
        },
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={handleOpenModal}
        className="flex items-center gap-2 bg-red-50 hover:bg-red-100 text-red-600 hover:text-red-700 transition-all duration-200 text-sm font-semibold px-4 py-2 rounded-xl border-2 border-red-200 hover:border-red-300 group shadow-sm hover:shadow-md"
      >
        <Flag className="w-4 h-4 group-hover:scale-110 transition-transform text-red-500" />
        <span>Denunciar</span>
      </button>

      {/* Modal de Denúncia */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-100 p-4 flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                Denunciar Anúncio
              </h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <p className="text-sm text-gray-500 mb-2">
                  Denunciando:{" "}
                  <strong className="text-gray-700">{postTitle}</strong>
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Motivo da denúncia *
                </label>
                <select
                  value={selectedReason}
                  onChange={(e) => setSelectedReason(e.target.value)}
                  className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-[#449879] focus:border-transparent"
                  required
                >
                  <option value="">Selecione um motivo...</option>
                  {REPORT_REASONS.map((reason) => (
                    <option key={reason} value={reason}>
                      {reason}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição (opcional)
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Descreva detalhadamente o problema..."
                  rows="4"
                  className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-[#449879] focus:border-transparent resize-none"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Forneça o máximo de detalhes para ajudar na análise.
                </p>
              </div>

              <div className="flex gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-xl hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleSubmitReport}
                  disabled={loading || !selectedReason}
                  className="flex-1 bg-red-500 hover:bg-red-600 text-white py-2 rounded-xl font-semibold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Flag className="w-4 h-4" />
                  )}
                  {loading ? "Enviando..." : "Enviar Denúncia"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>
        {`
                    @keyframes fadeIn {
                        from { opacity: 0; transform: scale(0.95); }
                        to { opacity: 1; transform: scale(1); }
                    }
                    .animate-fadeIn {
                        animation: fadeIn 0.25s ease-out;
                    }
                `}
      </style>
    </>
  );
}
